"use client";

import { ErrorMessage } from "@/components/catalyst/fieldset";
import { Label } from "@/components/catalyst/fieldset";
import { Input } from "@/components/catalyst/input";
import { useTranslations } from "next-intl";
import { useState, useEffect } from "react";

interface DiscountCodeInputProps {
  name: string;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  invalid?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  onChange?: (value: string) => void;
  testId?: string;
}

export function DiscountCodeInput({
  name,
  placeholder,
  defaultValue = "",
  value: controlledValue,
  invalid = false,
  error,
  label,
  required = false,
  disabled = false,
  onChange,
  testId,
}: DiscountCodeInputProps) {
  const t = useTranslations("DiscountCodeInput");
  const defaultLabel = label ?? t("label");
  const defaultPlaceholder = placeholder ?? t("placeholder");
  const [internalValue, setInternalValue] = useState(defaultValue);

  // Use controlled value if provided, otherwise use internal state
  const displayValue =
    controlledValue !== undefined ? controlledValue : internalValue;

  // Update internal state when defaultValue changes
  useEffect(() => {
    setInternalValue(defaultValue);
  }, [defaultValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Enforce uppercase and numbers only
    const filteredValue = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");

    if (onChange) {
      // Controlled mode: call parent onChange
      onChange(filteredValue);
    } else {
      // Uncontrolled mode: update internal state
      setInternalValue(filteredValue);
    }
  };

  return (
    <div>
      <Label>{defaultLabel}</Label>
      <Input
        name={name}
        placeholder={defaultPlaceholder}
        value={displayValue}
        onChange={handleChange}
        invalid={invalid}
        required={required}
        disabled={disabled}
        data-testid={testId}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
}
